import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { EmptyState, TableShell, Th } from "@/features/power_projects/components/ui";
import {
  useAddSuppression,
  useDeleteSuppression,
  useDoNotContact,
} from "@/features/leads/api/leads";

/**
 * Globalna lista „nie kontaktować”.
 *
 * Wpis jest kluczowany NIP-em i adresem, nie leadem — dzięki temu sprzeciw działa
 * także wtedy, gdy firma zostanie skasowana z bazy i znaleziona ponownie przez
 * kolejne wyszukiwanie.
 */

export function LeadSuppressionPage() {
  const [search, setSearch] = useState("");
  const { data: entries, isLoading } = useDoNotContact(search);
  const add = useAddSuppression();
  const remove = useDeleteSuppression();

  const [email, setEmail] = useState("");
  const [nip, setNip] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (!email.trim() && !nip.trim()) return setError("Podaj adres e-mail albo NIP.");
    setError("");
    add.mutate(
      { email: email.trim(), nip: nip.replace(/\D/g, ""), reason: reason.trim() },
      {
        onSuccess: () => {
          setEmail("");
          setNip("");
          setReason("");
        },
        onError: (err: unknown) => {
          const errors = (err as { response?: { data?: { errors?: Record<string, unknown> } } })
            .response?.data?.errors;
          const detail = errors && Object.values(errors)[0];
          setError(Array.isArray(detail) ? String(detail[0]) : "Nie udało się dodać wpisu.");
        },
      },
    );
  };

  return (
    <>
      <p className="section-label mb-3">
        Adresy i NIP-y, do których nie wolno pisać. Lista działa globalnie — niezależnie od
        kampanii i od tego, kto ją uruchamia — i przeżywa skasowanie leada z bazy.
      </p>

      <div className="card mb-5 flex flex-wrap items-center gap-2 p-3">
        <input
          className="input-base sm:w-56"
          placeholder="E-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          className="input-base sm:w-40"
          placeholder="NIP"
          value={nip}
          onChange={(e) => setNip(e.target.value)}
        />
        <input
          className="input-base min-w-[12rem] flex-1"
          placeholder="Powód"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <Button size="sm" onClick={submit} loading={add.isPending}>
          Dodaj wpis
        </Button>
      </div>
      {error && <p className="mb-3 text-sm text-[var(--color-primary)]">{error}</p>}

      <div className="relative mb-4 max-w-sm">
        <Search
          size={14}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj po adresie lub NIP…"
          className="input-base pl-8"
        />
      </div>

      {isLoading ? (
        <PageLoader />
      ) : (entries ?? []).length === 0 ? (
        <div className="card">
          <EmptyState>Lista jest pusta.</EmptyState>
        </div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Adres / NIP</Th>
              <Th>Powód</Th>
              <Th>Lead</Th>
              <Th>Dodano</Th>
              <Th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {(entries ?? []).map((entry) => (
              <tr key={entry.id} className="border-t border-[var(--color-border)]">
                <td className="px-3 py-2.5 font-mono">{entry.email || entry.nip}</td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">
                  {entry.reason || "—"}
                </td>
                <td className="px-3 py-2.5">
                  {entry.lead ? (
                    <Link to={`/leads/${entry.lead}`} className="hover:text-[var(--color-primary)]">
                      {entry.lead_name}
                    </Link>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-[var(--color-text-muted)]">
                  {format(new Date(entry.created_at), "dd.MM.yyyy")}
                  {entry.created_by_name ? ` · ${entry.created_by_name}` : ""}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button
                    type="button"
                    title="Zdejmij z listy"
                    onClick={() => {
                      if (!confirm("Zdjąć wpis z listy „nie kontaktować”?")) return;
                      remove.mutate(entry.id);
                    }}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}
    </>
  );
}
